import { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { Building2, Users, Globe, CalendarCheck, Calendar, MapPin, Scissors, Activity, Stethoscope, Heart } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Marker
const createIcon = (color, label, size = 32) =>
  L.divIcon({
    className: "odr-marker",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};border:3px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-size:${size < 30 ? 9 : 11}px;font-weight:700;
      font-family:Arial,sans-serif;letter-spacing:0.5px;
    ">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2 + 4)],
  });

const ICONS = {
  client: createIcon("#e91e63", "CE", 30),
  hairdresser: createIcon("#9c27b0", "PA", 30),
  agent: createIcon("#2d7a5a", "AG", 30),
  distributor: createIcon("#c9a96e", "DS", 30),
  physiotherapist: createIcon("#1976d2", "FT", 30),
  hospital: createIcon("#d32f2f", "OS", 30),
  association: createIcon("#ff9800", "AS", 30),
  // legacy
  upcoming: createIcon("#9c27b0", "PA", 30),
  past: createIcon("#1976d2", "FT", 30),
};

const LAYER_CONFIG = {
  client: { label: "Centri Estetici", color: "#e91e63", icon: Building2 },
  hairdresser: { label: "Parrucchieri", color: "#9c27b0", icon: Scissors },
  agent: { label: "Agenti", color: "#2d7a5a", icon: Users },
  distributor: { label: "Distributori", color: "#c9a96e", icon: Globe },
  physiotherapist: { label: "Fisioterapisti", color: "#1976d2", icon: Activity },
  hospital: { label: "Ospedali", color: "#d32f2f", icon: Stethoscope },
  association: { label: "Associazioni", color: "#ff9800", icon: Heart },
};

// Fallback layer se le settings non sono ancora caricate dal server
const FALLBACK_LAYERS = ["client", "hairdresser", "distributor"];

const FitBounds = ({ points, events }) => {
  const map = useMap();
  useEffect(() => {
    const allCoords = [
      ...points.filter((p) => p.lat && p.lng).map((p) => [p.lat, p.lng]),
      ...events.filter((e) => e.lat && e.lng).map((e) => [e.lat, e.lng]),
    ];
    if (allCoords.length > 0) {
      const bounds = L.latLngBounds(allCoords);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [points, events, map]);
  return null;
};

// Helpers per costruire URL social
const buildSocialLinks = (p) => {
  const links = [];
  if (p.website) {
    let u = p.website.trim();
    if (!u.startsWith("http")) u = "https://" + u;
    links.push({ type: "website", url: u });
  }
  if (p.instagram) {
    let u = p.instagram.trim();
    if (u.startsWith("@")) u = `https://instagram.com/${u.slice(1)}`;
    else if (!u.startsWith("http")) u = `https://instagram.com/${u.replace("instagram.com/", "")}`;
    links.push({ type: "instagram", url: u });
  }
  if (p.facebook) {
    let u = p.facebook.trim();
    if (!u.startsWith("http")) u = `https://facebook.com/${u.replace("facebook.com/", "")}`;
    links.push({ type: "facebook", url: u });
  }
  if (p.tiktok) {
    let u = p.tiktok.trim();
    if (u.startsWith("@")) u = `https://tiktok.com/${u}`;
    else if (!u.startsWith("http")) u = `https://tiktok.com/@${u.replace("tiktok.com/", "").replace("@", "")}`;
    links.push({ type: "tiktok", url: u });
  }
  if (p.whatsapp) {
    const digits = p.whatsapp.replace(/\D/g, "");
    if (digits.length >= 6) links.push({ type: "whatsapp", url: `https://wa.me/${digits}` });
  }
  return links;
};

const SOCIAL_ICON_SVG = {
  website: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
  instagram: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>',
  facebook: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>',
  tiktok: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.39a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.4z"/></svg>',
  whatsapp: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/></svg>',
};

const SOCIAL_COLORS = {
  website: "#666",
  instagram: "#E1306C",
  facebook: "#1877F2",
  tiktok: "#000",
  whatsapp: "#25D366",
};

const buildSocialIconsHTML = (point) => {
  const items = [
    { has: !!point.website, url: point.website, type: "website" },
    { has: !!(point.has_instagram || point.instagram), url: point.instagram, type: "instagram" },
    { has: !!(point.has_facebook || point.facebook), url: point.facebook, type: "facebook" },
    { has: !!(point.has_tiktok || point.tiktok), url: point.tiktok, type: "tiktok" },
    { has: !!(point.has_whatsapp || point.whatsapp), url: point.whatsapp, type: "whatsapp" },
  ].filter(i => i.has);
  if (!items.length) return "";

  return `<div style="display:flex;gap:8px;margin-top:8px;align-items:center;flex-wrap:wrap;">
    ${items
      .map((it) => {
        const links = buildSocialLinks({ [it.type]: it.url });
        const realLink = links.find(l => l.type === it.type);
        if (realLink) {
          return `<a href="${realLink.url}" target="_blank" rel="noopener noreferrer"
            style="color:${SOCIAL_COLORS[it.type]};display:inline-flex;align-items:center;text-decoration:none;transition:opacity 0.2s;"
            onmouseover="this.style.opacity=0.7"
            onmouseout="this.style.opacity=1"
            title="${it.type}">${SOCIAL_ICON_SVG[it.type]}</a>`;
        }
        // Icona grigia senza link
        return `<span
            style="color:#bbb;display:inline-flex;align-items:center;cursor:not-allowed;"
            title="${it.type} (link da inserire)">${SOCIAL_ICON_SVG[it.type]}</span>`;
      })
      .join("")}
  </div>`;
};

const PublicNetworkMap = () => {
  const [mapData, setMapData] = useState({ points: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [activeLayers, setActiveLayers] = useState(FALLBACK_LAYERS);
  const [visibleLayers, setVisibleLayers] = useState(FALLBACK_LAYERS);

  // Carica le settings dinamiche dall'admin (quali layer mostrare come pulsanti)
  useEffect(() => {
    axios.get(`${API}/odr-network/public/settings`).then((res) => {
      const enabled = res.data?.enabled_layers || FALLBACK_LAYERS;
      setVisibleLayers(enabled);
      setActiveLayers(enabled);
    }).catch(() => {
      // fallback silenzioso
    });
  }, []);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/odr-network/public/map-data`, {
        params: { layers: activeLayers.join(",") },
      });
      setMapData(res.data);
    } catch (e) {
      // silente: in iframe non vogliamo toast
    } finally {
      setLoading(false);
    }
  }, [activeLayers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Killa qualsiasi widget chat Emergent che si aggiunge dinamicamente
  useEffect(() => {
    const killChat = () => {
      const all = document.body.querySelectorAll("*");
      for (let i = 0; i < all.length; i++) {
        const el = all[i];
        if (el.tagName === "IFRAME" && el.title && el.title.toLowerCase().includes("network")) continue;
        if (el.closest(".odr-public-map")) continue;
        const s = window.getComputedStyle(el);
        if (s.position !== "fixed" && s.position !== "sticky") continue;
        const r = el.getBoundingClientRect();
        // Widget piccoli ancorati ai bordi
        if (r.height < 250 && r.width < 400 &&
            (r.bottom > window.innerHeight - 250 || r.top < 200) &&
            (r.right > window.innerWidth - 250 || r.left < 250)) {
          el.style.setProperty("display", "none", "important");
          el.style.setProperty("visibility", "hidden", "important");
        }
      }
    };
    killChat();
    const t1 = setTimeout(killChat, 500);
    const t2 = setTimeout(killChat, 2000);
    const t3 = setTimeout(killChat, 5000);
    let observer;
    if (window.MutationObserver) {
      observer = new MutationObserver(killChat);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (observer) observer.disconnect();
    };
  }, []);

  const toggleLayer = (layer) =>
    setActiveLayers((prev) =>
      prev.includes(layer) ? prev.filter((l) => l !== layer) : [...prev, layer]
    );

  return (
    <div className="w-full h-screen bg-[#faf8f5] flex flex-col overflow-hidden odr-public-map" data-testid="public-network-map">
      {/* CSS: nascondi chat widget Emergent solo su questa pagina */}
      <style>{`
        body { margin: 0 !important; padding: 0 !important; }
        #emergent-chat-widget,
        [class*="emergent"][class*="chat"],
        [id*="emergent"][id*="chat"],
        .chat-widget,
        iframe[src*="chat"],
        [class*="floating-chat"],
        [data-testid*="chat-widget"] {
          display: none !important;
          visibility: hidden !important;
        }
      `}</style>

      {/* Header */}
      <div className="text-white px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 shrink-0 shadow-md" style={{ background: "linear-gradient(135deg, #D4A5A5 0%, #B87A8A 50%, #A06978 100%)" }}>
        <div className="flex items-center gap-2 shrink-0">
          <img
            src="/odr-assets/odr_logo_circle.jpeg"
            alt="ODR"
            className="w-9 h-9 rounded-full border-2 border-white/70 shadow-sm"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="leading-tight">
            <h1 className="text-sm font-bold tracking-wide">ODR Global Network</h1>
            <p className="text-[10px] text-white/85">Centri estetici e parrucchieri</p>
          </div>
        </div>

        {/* Layer toggles - centrati */}
        <div className="flex items-center gap-2 flex-wrap justify-center flex-1">
          {Object.entries(LAYER_CONFIG)
            .filter(([key]) => visibleLayers.includes(key))
            .map(([key, cfg]) => {
            const Icon = cfg.icon;
            const active = activeLayers.includes(key);
            return (
              <button
                key={key}
                onClick={() => toggleLayer(key)}
                data-testid={`layer-toggle-${key}`}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all border-2 whitespace-nowrap shadow-sm ${
                  active
                    ? "bg-white text-[#7A4856] border-white"
                    : "bg-white/10 border-white/40 text-white/80 line-through"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 z-[400] flex items-center justify-center bg-white/70">
            <div className="text-[#5a3d7a] text-sm font-medium">Caricamento mappa...</div>
          </div>
        )}
        <MapContainer
          center={[42.5, 12.5]}
          zoom={6}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={mapData.points} events={mapData.events} />

          {mapData.points.map((p) => (
            <Marker
              key={p.point_id}
              position={[p.lat, p.lng]}
              icon={ICONS[p.type] || ICONS.client}
            >
              <Popup>
                <div style={{ minWidth: 220 }}>
                  {p.logo_url && (
                    <div style={{ background: "#fff", padding: 8, border: "1px solid #f0f0f0", borderRadius: 6, marginBottom: 8, minHeight: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <img
                        src={p.logo_url}
                        alt=""
                        style={{ maxHeight: 70, maxWidth: "100%", objectFit: "contain" }}
                        onError={(ev) => { ev.target.parentNode.style.display = "none"; }}
                      />
                    </div>
                  )}
                  <div style={{ fontWeight: 700, color: "#2d1b4e", marginBottom: 4 }}>
                    {p.name}
                  </div>
                  {p.address && (
                    <div style={{ fontSize: 12, color: "#444", marginBottom: 2 }}>
                      <MapPin size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
                      {p.address}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#666" }}>
                    {[p.city, p.region, p.country].filter(Boolean).join(" · ")}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: LAYER_CONFIG[p.type]?.color || "#666",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {LAYER_CONFIG[p.type]?.label || p.type}
                  </div>
                  {p.accademia_user_id && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        background: "#7b5ea7",
                        color: "#fff",
                        fontSize: "10px",
                        padding: "3px 8px",
                        borderRadius: "999px",
                        marginBottom: "6px",
                        fontWeight: 600,
                      }}
                      title="Account Accademia attivato"
                    >
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14, height: 14, borderRadius: "50%",
                        background: "#fff", color: "#7b5ea7",
                        fontSize: 9, fontWeight: 700,
                      }}>A</span>
                      Videocorsi Academy
                    </div>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: buildSocialIconsHTML(p) }} />
                </div>
              </Popup>
            </Marker>
          ))}

          {mapData.events.map((e) => (
            <Marker
              key={e.event_id}
              position={[e.lat, e.lng]}
              icon={ICONS[e.status] || ICONS.upcoming}
            >
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <div style={{ fontWeight: 700, color: "#2d1b4e", marginBottom: 4 }}>
                    {e.title}
                  </div>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    <MapPin size={11} style={{ display: "inline", marginRight: 4 }} />
                    {[e.city, e.country].filter(Boolean).join(", ")}
                  </div>
                  {e.event_date && (
                    <div style={{ fontSize: 11, marginTop: 4, color: "#999" }}>
                      📅 {e.event_date}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default PublicNetworkMap;
